import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';
import type { ShopTaxonomy } from '@shared/api/hooks/useTenantAdminConfig';

// ─── Tipi ─────────────────────────────────────────────────────────────────────

/** Riepilogo di un prodotto in primo piano, come restituito da GET /shop/settings. */
export interface FeaturedProductSummary {
  id: string;
  /** Nome localizzato (JSON free-form: { it?, en? }). null se il prodotto non esiste più. */
  name: unknown | null;
  sku: string | null;
  price: number | null;
  images: string[];
  isAvailable: boolean;
  /** T-81: true se l'id non corrisponde (più) a un prodotto del tenant. */
  missing?: boolean;
}

/** Risposta di GET/PATCH /shop/settings. */
export interface ShopSettingsDto {
  /** Tassonomia grezza (può essere null → usare il default). */
  taxonomy: ShopTaxonomy | null;
  /** RETROCOMPAT: primo elemento della lista (o null). */
  featuredProductId: string | null;
  /** T-81: lista ORDINATA di id in primo piano. */
  featuredProductIds: string[];
  /** RETROCOMPAT: primo prodotto risolto (o null). */
  featuredProduct: FeaturedProductSummary | null;
  /** T-81: lista risolta e ordinata, con flag isAvailable/missing. */
  featuredProducts: FeaturedProductSummary[];
}

/** Body di PATCH /shop/settings. `featuredProductIds` (T-81) ha precedenza sul singolo. */
export interface UpdateShopSettingsInput {
  taxonomy?: ShopTaxonomy;
  /** RETROCOMPAT scrittura: singolo (null per azzerare). */
  featuredProductId?: string | null;
  /** T-81: lista ORDINATA (vuota = nessun featured). */
  featuredProductIds?: string[];
}

const QK = ['shop-settings'] as const;

export function useShopSettings() {
  return useQuery({
    queryKey: QK,
    queryFn: async () => {
      const res = await apiClient.get<ShopSettingsDto>('/shop/settings');
      return res.data;
    },
  });
}

export function useUpdateShopSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateShopSettingsInput) => {
      const res = await apiClient.patch<ShopSettingsDto>('/shop/settings', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
